import type {
  AcceptOrderConfirmationRequest,
  CancelOrderProductsRequest,
  CreateOrderConfirmationRequest,
  ListOrdersResponseItem,
  MoveOrderProductsRequest,
  OrderBookingResponse,
  OrderBookingsListResponse,
  OrderConfirmationResponse,
  OrderConfirmationsListResponse,
  OrderEmailsResponse,
  OrderEmailType,
  OrderPriceByDepartmentResponse,
  OrderProductAfterActionListResponse,
  OrderResponse,
  OrdersListResponse,
  RestoreOrderProductsRequest,
  SendOrderEmailRequest,
  UpdateContactPersonRequest,
  UpdateOrderNameRequest,
  UpdateOrderPaymentsRequest,
  UpdateOrderRequest,
  UpdateOrderTodosRequest,
} from "@przezmorze/api-types";
import { isAxiosError } from "axios";
import { przezmorzeApi, throwMessageOnBadRequestStatus, throwMessageOnStatuses } from "../../api/api";
import type { OrderStatus as DomainOrderStatus } from "../data/BaseEntity";
import type { SavedOrder, SavedOrderProduct, SavedOrdersListItem } from "./orderTypes";

export type {
  CreateOrderConfirmationRequest,
  DepartmentPriceBreakdown as DepartmentAmounts,
  OrderEmailNotification as EmailForOrder,
  InvoiceSellerId,
  OrderBookingResponse,
  OrderConfirmationResponse,
  OrderEmailType,
  OrderPriceByDepartmentResponse,
  OrderPriceValue,
  OrderTodos,
  OrderPayment as PaymentDTO,
  SellerPriceBreakdown as SellerLimits,
} from "@przezmorze/api-types";

export type CreateOrUpdateOrderRequest = UpdateOrderRequest;

interface ParticipantListTemplate {
  readonly name: string;
  readonly url: string;
}

export interface ParticipantListTemplatesResponse {
  readonly listTemplates: ParticipantListTemplate[];
}

export async function createOrder(
  request: Omit<CreateOrUpdateOrderRequest, "todos">,
  abortSignal?: AbortSignal,
): Promise<SavedOrder> {
  try {
    const response = await przezmorzeApi.post<OrderResponse>("/orders", request, {
      signal: abortSignal,
    });
    return orderResponseToSavedOrder(response.data);
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateOrder(
  orderId: string,
  request: CreateOrUpdateOrderRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrder> {
  try {
    const response = await przezmorzeApi.put<OrderResponse>(`/orders/${orderId}`, request, {
      signal: abortSignal,
    });
    return orderResponseToSavedOrder(response.data);
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function updateOrderPayments(
  orderId: string,
  request: UpdateOrderPaymentsRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrder> {
  try {
    const response = await przezmorzeApi.put<OrderResponse>(`/orders/${orderId}/payments`, request, {
      signal: abortSignal,
    });
    return orderResponseToSavedOrder(response.data);
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateOrderTodos(
  orderId: string,
  request: UpdateOrderTodosRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrder> {
  try {
    const response = await przezmorzeApi.put<OrderResponse>(`/orders/${orderId}/todos`, request, {
      signal: abortSignal,
    });
    return orderResponseToSavedOrder(response.data);
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function getOrder(orderId: string, abortSignal?: AbortSignal): Promise<SavedOrder> {
  const response = await przezmorzeApi.get<OrderResponse>(`/orders/${orderId}`, { signal: abortSignal });
  return orderResponseToSavedOrder(response.data);
}

export async function getOrderParticipantListTemplates(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<ParticipantListTemplate[]> {
  const response = await przezmorzeApi.get<ParticipantListTemplatesResponse>(
    `/orders/${orderId}/participant-list-templates`,
    {
      signal: abortSignal,
    },
  );
  return response.data.listTemplates;
}

export async function getEmailsSentForOrder(orderId: string, abortSignal?: AbortSignal) {
  const response = await przezmorzeApi.get<OrderEmailsResponse>(`/orders/${orderId}/emails`, {
    signal: abortSignal,
  });
  return response.data.emails;
}

export async function deleteOrder(orderId: string, abortSignal?: AbortSignal) {
  try {
    await przezmorzeApi.delete(`/orders/${orderId}`, { signal: abortSignal });
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function listOrders(
  params: { from: string; to?: string; showHistorical: boolean },
  abortSignal?: AbortSignal,
) {
  const response = await przezmorzeApi.get<OrdersListResponse>("/orders", {
    signal: abortSignal,
    params: { from: params.from, to: params.to, showHistorical: params.showHistorical ? "true" : "false" },
  });
  return response.data.items.map((o) => listOrdersResponseItemToSavedOrder(o));
}

function orderResponseToSavedOrder({ id, customer, products, status, ...others }: OrderResponse): SavedOrder {
  return {
    ...others,
    id: id.value,
    userFriendlyId: id.userFriendly,
    customerId: customer.id,
    customerContactPersonEmail: customer.contactPersonEmail,
    status: status as DomainOrderStatus,
    products: products.map(({ booking, ...other }) => ({
      ...other,
      bookingNumber: booking?.bookingNumber,
      bookingId: booking?.id,
    })),
  };
}

function listOrdersResponseItemToSavedOrder({
  id,
  customer,
  products,
  status,
  ...others
}: ListOrdersResponseItem): SavedOrdersListItem {
  return {
    ...others,
    id: id.value,
    userFriendlyId: id.userFriendly,
    customerId: customer.id,
    customerContactPersonEmail: customer.contactPersonEmail,
    status: status as DomainOrderStatus,
    products: products.map(({ booking, ...other }) => ({
      ...other,
      bookingNumber: booking?.bookingNumber,
      bookingId: booking?.id,
    })),
  };
}

export async function createOrderConfirmation(
  orderId: string,
  requestBody: CreateOrderConfirmationRequest,
  abortSignal?: AbortSignal,
): Promise<OrderConfirmationResponse> {
  try {
    const response = await przezmorzeApi.post<OrderConfirmationResponse>(
      `/orders/${orderId}/confirmations`,
      requestBody,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function listOrderConfirmations(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<OrderConfirmationResponse[]> {
  const response = await przezmorzeApi.get<OrderConfirmationsListResponse>(`/orders/${orderId}/confirmations`, {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function getOrderConfirmation(
  orderId: string,
  confirmationId: string,
  abortSignal?: AbortSignal,
): Promise<OrderConfirmationResponse | null> {
  try {
    const response = await przezmorzeApi.get<OrderConfirmationResponse>(
      `/orders/${orderId}/confirmations/${confirmationId}`,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function deleteOrderConfirmation(orderId: string, confirmationId: string, abortSignal?: AbortSignal) {
  await przezmorzeApi.delete(`/orders/${orderId}/confirmations/${confirmationId}`, {
    signal: abortSignal,
  });
}

export async function acceptOrderConfirmation(
  orderId: string,
  confirmationId: string,
  nameAndSurname: string,
  abortSignal?: AbortSignal,
) {
  try {
    const response = await przezmorzeApi.post<OrderConfirmationResponse>(
      `/orders/${orderId}/confirmations/${confirmationId}/accept`,
      { nameAndSurname } satisfies AcceptOrderConfirmationRequest,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function sendOrderEmail(orderId: string, type: OrderEmailType, abortSignal?: AbortSignal) {
  try {
    const response = await przezmorzeApi.post<Record<string, never>>(
      `/orders/${orderId}/emails`,
      { type } satisfies SendOrderEmailRequest,
      {
        signal: abortSignal,
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function getOrdersCsv(
  from: string,
  to: string,
  showHistorical: boolean,
  abortSignal?: AbortSignal,
): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(`/order-export/csv`, {
    signal: abortSignal,
    headers: { Accept: "application/csv" },
    params: { from, to, showHistorical },
    responseType: "blob",
  });
  return response.data;
}

export async function getOrderProductsCsv(
  from: string,
  to: string,
  showHistorical: boolean,
  abortSignal?: AbortSignal,
): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>(`/order-export/products-csv`, {
    signal: abortSignal,
    headers: { Accept: "application/csv" },
    params: { from, to, showHistorical },
    responseType: "blob",
  });
  return response.data;
}

export async function getOrderBookings(orderId: string, abortSignal?: AbortSignal): Promise<OrderBookingResponse[]> {
  const response = await przezmorzeApi.get<OrderBookingsListResponse>(`/orders/${orderId}/bookings`, {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function cancelOrderBooking(orderId: string, id: string, abortSignal?: AbortSignal): Promise<void> {
  await przezmorzeApi.delete(`/orders/${orderId}/bookings/${id}`, { signal: abortSignal });
}

export async function cancelOrderProduct(
  orderId: string,
  request: CancelOrderProductsRequest,
  abortSignal?: AbortSignal,
): Promise<void> {
  try {
    await przezmorzeApi.post(`/orders/${orderId}/products/cancel`, request, { signal: abortSignal });
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function restoreOrderProducts(
  orderId: string,
  request: RestoreOrderProductsRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrderProduct[]> {
  try {
    const response = await przezmorzeApi.post<OrderProductAfterActionListResponse>(
      `/orders/${orderId}/products/restore`,
      request,
      { signal: abortSignal },
    );
    return response.data.items.map(({ booking, ...other }) => ({
      ...other,
      bookingNumber: booking?.bookingNumber,
    }));
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function moveOrderProducts(
  orderId: string,
  request: MoveOrderProductsRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrderProduct[]> {
  try {
    const response = await przezmorzeApi.post<OrderProductAfterActionListResponse>(
      `/orders/${orderId}/products/move`,
      request,
      { signal: abortSignal },
    );
    return response.data.items.map(({ booking, ...other }) => ({
      ...other,
      bookingNumber: booking?.bookingNumber,
    }));
  } catch (error) {
    throwMessageOnStatuses(error, [400, 409]);
  }
}

export async function getOrderPriceByDepartment(
  orderId: string,
  abortSignal?: AbortSignal,
): Promise<OrderPriceByDepartmentResponse> {
  const response = await przezmorzeApi.get<OrderPriceByDepartmentResponse>(`/orders/${orderId}/price-by-department`, {
    signal: abortSignal,
  });
  return response.data;
}

export async function updateContactPerson(
  orderId: string,
  request: UpdateContactPersonRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrder> {
  try {
    const response = await przezmorzeApi.put<OrderResponse>(`/orders/${orderId}/contactPerson`, request, {
      signal: abortSignal,
    });
    return orderResponseToSavedOrder(response.data);
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateOrderName(
  orderId: string,
  request: UpdateOrderNameRequest,
  abortSignal?: AbortSignal,
): Promise<SavedOrder> {
  try {
    const response = await przezmorzeApi.put<OrderResponse>(`/orders/${orderId}/orderName`, request, {
      signal: abortSignal,
    });
    return orderResponseToSavedOrder(response.data);
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}
